import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import TriEventsLoadingScreen from '@/components/common/TriEventsLoadingScreen';

// Shadcn components
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";

// Types
import { Event, TicketInterface } from "@/types/ticket/ticketValidation";

// Icons
import { Ticket, CheckCircle2, Calendar, User, Mail, Building2, Clock, MapPin } from "lucide-react";

// Services
import { getEventDetails } from '@/services/event/fetchEventDetails';
import { getTicketByUuid } from '@/services/ticket/getTicket'
import { burnTicketService } from '@/services/ticket/burnTicket'

const TicketValidation: React.FC = () => {
    const { uuid } = useParams<{ uuid: string }>();
    const [ticket, setTicket] = useState<TicketInterface | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [burnMessage, setBurnMessage] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [eventName, setEventName] = useState<string>('');
    const [eventDetails, setEventDetails] = useState<Event | null>(null);

    useEffect(() => {
        const fetchTicket = async (): Promise<void> => {
            try {
                const data = await getTicketByUuid(uuid!);
                if (!data.valid) {
                    setError('Ticket has already been used.');
                    return;
                }
                setTicket(data.ticket);
                
                // Fetch event details using event_uuid
                if (data.ticket.event_uuid) {
                    try {
                        const eventData = await getEventDetails(data.ticket.event_uuid);
                        setEventName(eventData.title);
                        setEventDetails(eventData);
                    } catch (e) {
                        setError("Could not fetch event details.");
                    }
                }
            } catch (err) {
                setError('An error occurred while validating the ticket. ' + (err instanceof Error ? err.message : String(err)));
            } finally {
                setIsLoading(false);
            }
        };

        fetchTicket();
    }, [uuid]);

    const burnTicket = async (): Promise<void> => {
        try {
            const data = await burnTicketService(uuid!);
            if (data.burned) {
                setBurnMessage('Ticket has been successfully burned.');
                setTicket(prevTicket => prevTicket ? { ...prevTicket, ticket_burned: true } : null);
            } else {
                setError('Failed to burn the ticket.');
            }
        } catch (err) {
            setError('An error occurred while burning the ticket. ' + (err instanceof Error ? err.message : String(err)));
        }
    };

    const formatDate = (dateString: string) => {
        return dateString.split('T')[0];
    };

    if (isLoading) {
        return (
            <TriEventsLoadingScreen />
        );
    }

    if (error) {
        const isAccessDenied = error.includes('do not have access to this ticket');
        const isNotLoggedIn = error.includes('Please log in to your organization');
        
        return (
            <div className="relative min-h-screen bg-gradient-to-br from-blue-50 via-white to-teal-50 overflow-x-hidden flex items-center justify-center">
                <div className="container mx-auto px-4 py-8 relative z-10">
                    <div className="max-w-lg mx-auto">
                        <Card className="w-full shadow-xl border-0 bg-white/90 backdrop-blur-md">
                            <CardHeader>
                                <CardTitle className="text-2xl text-center text-black">
                                    {isNotLoggedIn ? 'Authentication Required' : isAccessDenied ? 'Access Denied' : 'Invalid Ticket'}
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <Alert variant={isNotLoggedIn || isAccessDenied ? "default" : "destructive"} className={isNotLoggedIn || isAccessDenied ? "bg-blue-50 border-blue-200" : "bg-red-50 border-red-200"}>
                                    <div className="flex items-center">
                                        <AlertDescription className={isNotLoggedIn || isAccessDenied ? "text-blue-700" : "text-red-700"}>
                                            {error}
                                        </AlertDescription>
                                    </div>
                                </Alert>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>
        );
    }

    if (!ticket) {
        return (
            <div className="relative min-h-screen bg-gradient-to-br from-blue-50 via-white to-teal-50 overflow-x-hidden flex items-center justify-center">
                <div className="container mx-auto px-4 py-8 relative z-10">
                    <div className="max-w-lg mx-auto">
                        <Card className="w-full shadow-xl border-0 bg-white/90 backdrop-blur-md">
                            <CardHeader>
                                <CardTitle className="text-2xl text-center text-black">
                                    Ticket Not Found
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <Alert variant="destructive" className="bg-red-50 border-red-200">
                                    <div className="flex items-center">
                                        <AlertDescription className="text-red-700">
                                            The requested ticket could not be found.
                                        </AlertDescription>
                                    </div>
                                </Alert>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="relative min-h-screen bg-gradient-to-br from-blue-50 via-white to-teal-50 overflow-x-hidden flex items-center justify-center">
            <div className="container mx-auto px-4 py-8 relative z-10">
                <div className="max-w-2xl mx-auto">
                    <Card className="w-full shadow-xl border-0 bg-white/90 backdrop-blur-md">
                        <CardHeader className="space-y-4 border-b border-gray-100">
                            <div className="flex items-center justify-between">
                                <div>
                                    <CardTitle className="text-3xl text-black">{eventName || 'Event Ticket'}</CardTitle>
                                    <div className="space-y-2 mt-3">
                                        <div className="flex items-center text-black">
                                            <Calendar className="h-4 w-4 mr-2" />
                                            <p className="text-sm">{eventDetails?.date && formatDate(eventDetails.date)}</p>
                                        </div>
                                        <div className="flex items-center text-black">
                                            <Clock className="h-4 w-4 mr-2" />
                                            <p className="text-sm">
                                                {eventDetails?.time_start && eventDetails?.time_end 
                                                    ? `${eventDetails.time_start} - ${eventDetails.time_end}`
                                                    : eventDetails?.time_start || 'Time not specified'}
                                            </p>
                                        </div>
                                        <div className="flex items-center text-black">
                                            <MapPin className="h-4 w-4 mr-2" />
                                            <p className="text-sm">{eventDetails?.location}</p>
                                        </div>
                                    </div>
                                    <CardDescription className="text-base text-gray-600 mt-3">
                                        Purchased on {new Date(ticket.createdAt).toLocaleDateString()}
                                    </CardDescription>
                                </div>
                                <Badge 
                                    variant={ticket.ticket_burned ? "destructive" : "secondary"}
                                    className={`text-base px-4 py-1.5 ${ticket.ticket_burned ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700"}`}
                                >
                                    {ticket.ticket_burned ? 'Used' : 'Valid'}
                                </Badge>
                            </div>
                        </CardHeader>
                        <CardContent className="space-y-6 pt-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <div className="flex items-center text-black">
                                        <User className="h-4 w-4 mr-2" />
                                        <p className="text-sm font-medium">Customer Name</p>
                                    </div>
                                    <p className="text-lg text-black">{ticket.customer_name}</p>
                                </div>
                                <div className="space-y-2">
                                    <div className="flex items-center text-black">
                                        <Mail className="h-4 w-4 mr-2" />
                                        <p className="text-sm font-medium">Customer Email</p>
                                    </div>
                                    <p className="text-lg text-black">{ticket.customer_email}</p>
                                </div>
                                <div className="space-y-2">
                                    <div className="flex items-center text-black">
                                        <Ticket className="h-4 w-4 mr-2" />
                                        <p className="text-sm font-medium">Ticket Type</p>
                                    </div>
                                    <p className="text-lg text-black">{ticket.price?.nickname || 'Standard'}</p>
                                </div>
                                <div className="space-y-2">
                                    <div className="flex items-center text-black">
                                        <Building2 className="h-4 w-4 mr-2" />
                                        <p className="text-sm font-medium">Organization</p>
                                    </div>
                                    <p className="text-lg text-black">{ticket.organization?.name}</p>
                                </div>
                            </div>
                            {burnMessage && (
                                <Alert className="bg-green-50 border-green-200">
                                    <div className="flex items-center">
                                        <CheckCircle2 className="h-5 w-5 mr-2 text-green-600" />
                                        <AlertDescription className="text-green-700">{burnMessage}</AlertDescription>
                                    </div>
                                </Alert>
                            )}
                        </CardContent>
                        {!ticket.ticket_burned && (
                            <CardFooter className="border-t border-gray-100 pt-6">
                                <Button 
                                    onClick={burnTicket} 
                                    className="w-full bg-[#23d6e4] hover:bg-[#1bc4d1] text-white text-lg font-semibold shadow-md border-0"
                                    size="lg"
                                >
                                    Validate & Burn Ticket
                                </Button>
                            </CardFooter>
                        )}
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default TicketValidation;